<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageMessagingSequences.title') }}
    </portal>
    <template v-if="messagingSequences !== null && hasLicense('Messaging')">
      <card :title="$t('admin.pageMessagingSequences.card.messagingSequences')" cols="12">
        <hm-card-inner v-for="(messagingSequence, idx) in messagingSequences"
                       :title="getName(idx, messagingSequence)"
                       :deletable="true"
                       :key="messagingSequence.id"
                       @delete="deleteMessagingSequence(messagingSequence)">
          <hm-col v-if="messagingSequence.sendEmailIfCreatedLate" cols="12">
            <span class="warning-text">
              {{ $t('admin.pageMessagingSequences.hintSendLateMightCauseResendsOnChange') }}
            </span>
          </hm-col>
          <hm-input-select id="templateId"
                           v-model="messagingSequence.templateId"
                           :label="$t('admin.pageMessagingSequences.templateId')"
                           name="templateId"
                           cols="12 6@tablet 3@desktop 2@hd start-1">
            <option v-for="template in templates" :key="template.id" :value="template.id">{{ template.name }}</option>
          </hm-input-select>
          <hm-input-number id="offsetPeriod"
                           v-model="messagingSequence.offsetPeriod"
                           :label="$t('admin.pageMessagingSequences.offsetPeriod')"
                           :disabled="messagingSequence.sendEmailIfCreatedLate === true && !hasRole('ROLE_SYSADMIN')"
                           :required="true"
                           cols="12 6@tablet 3@desktop 2@hd" />
          <hm-input-select id="offsetType"
                           v-model="messagingSequence.offsetType"
                           :label="$t('admin.pageMessagingSequences.offsetType')"
                           :disabled="messagingSequence.sendEmailIfCreatedLate === true && !hasRole('ROLE_SYSADMIN')"
                           name="offsetType"
                           enum-name="OffsetType"
                           cols="12 6@tablet 3@desktop 2@hd" />
          <hm-input-select id="field"
                           v-model="messagingSequence.field"
                           :label="$t('admin.pageMessagingSequences.field')"
                           name="field"
                           cols="12 6@tablet 3@desktop 2@hd">
            <option value="ArrivalDate">{{ $t('MessagingField.ArrivalDate') }}</option>
            <option value="DepartureDate">{{ $t('MessagingField.DepartureDate') }}</option>
            <option value="ReservationExpirationDate">{{ $t('MessagingField.ReservationExpirationDate') }}</option>
          </hm-input-select>
          <hm-input-mask id="timeOfDay"
                         v-model="messagingSequence.timeOfDay"
                         :validate="'required|regex:^(([0-1][0-9])\\|2[0-3]):[0-5][0-9]$'"
                         :label="$t('admin.pageMessagingSequences.timeOfDay')"
                         mask="##:##"
                         type="text"
                         cols="12 6@tablet 3@desktop 2@hd"
                         name="timeOfDay"/>
          <hm-input-toggle id="onlySendOncePerReservationGroup"
                           v-model="messagingSequence.onlySendOncePerReservationGroup"
                           :label="$t('admin.pageMessagingSequences.onlySendOncePerReservationGroup')"
                           :enabled-title="$t('admin.pageMessagingSequences.onlySendOncePerReservationGroupEnabled')"
                           :disabled-title="$t('admin.pageMessagingSequences.onlySendOncePerReservationGroupDisabled')"
                           :disabled="!isEmpty(messagingSequence.additionalConditions) && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           name="onlySendOncePerReservationGroup"
                           cols="12 6@tablet 3@desktop 2@hd start-1" />
          <hm-input-toggle id="sendEmailIfCreatedLate"
                           v-if="['Before'].includes(messagingSequence.offsetType)"
                           v-model="messagingSequence.sendEmailIfCreatedLate"
                           :label="$t('admin.pageMessagingSequences.sendEmailIfCreatedLate')"
                           :enabled-title="$t('admin.pageMessagingSequences.sendEmailIfCreatedLateEnabled')"
                           :disabled-title="$t('admin.pageMessagingSequences.sendEmailIfCreatedLateDisabled')"
                           :disabled="!hasRole('ROLE_SYSADMIN')"
                           name="onlySendOncePerReservationGroup"
                           cols="12 6@tablet 3@desktop 2@hd" />
          <hm-textarea v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || !isEmpty(messagingSequence.additionalConditions)"
                       id="additionalConditions"
                       v-model="messagingSequence.additionalConditions"
                       :input-rows="isEmpty(messagingSequence.additionalConditions) ? 2 : (messagingSequence.additionalConditions).split('\n').length + 1"
                       :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || additionalConditionsLocked"
                       :no-min-height="true"
                       label="Additional Conditions (Support-Only)"
                       name="additionalConditions"
                       cols="12 start-1"
                       @click="additionalConditionsLocked = false" />
        </hm-card-inner>
      </card>
    </template>

    <hm-floating-buttons id="actions" ref="floatingButtons">
      <hm-floating-button :label="$t('admin.pageMessagingSequences.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('admin.pageMessagingSequences.actions.addPreArrivalEmail')" icon="plus" color="blue" @click="addPreArrivalEmail()" />
        <hm-floating-child-button :label="$t('admin.pageMessagingSequences.actions.addPostDepartureEmail')" icon="plus" color="blue" @click="addPostDepartureEmail()" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isEqual, isNil, keyBy, map, maxBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmInputNumber from '../../lib/inputs/HmInputNumber';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import HmTextarea from '../../lib/inputs/HmTextarea';

export default {
  name: 'PageMessagingSequences',
  components: { HmTextarea, HmInputToggle, HmInputNumber },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'messagingSequences',
      variable: 'messagingSequences',
      fn: function (cbSaveComplete) {
        if (isNil(this.messagingSequences)) {
          cbSaveComplete();
          return;
        }

        const messagingSequences = cloneDeep(this.messagingSequences);
        const savedMessagingSequencesMap = isNil(this.savedMessagingSequences) ? {} : keyBy(this.savedMessagingSequences, 'id');

        messagingSequences
          .filter((it) => {
            if (isNil(savedMessagingSequencesMap[it.id])) return false;
            if (isEqual(savedMessagingSequencesMap[it.id], it)) return false;

            return true;
          })
          .forEach((it) => {
            const toSave = cloneDeep(it);
            toSave.offsetPeriod = `P${isNil(toSave.offsetPeriod) ? 1 : toSave.offsetPeriod}D`;
            this.axios.put(this.serviceUrls.messagingSequence.get(this, this.messagingSequenceType, it.id), toSave)
              .then((response) => {
                this.showMessages(response);
              })
              .catch((error) => {
                this.showMessages(error.response);
              });
          });

        this.savedMessagingSequences = cloneDeep(messagingSequences);

        cbSaveComplete();
      },
    },
  ],

  data() {
    return {
      messagingSequences: null,
      savedMessagingSequences: null,
      messagingSequenceType: 'RoomReservation',
      templates: [],
      additionalConditionsLocked: true,
    };
  },

  computed: {
  },

  watch: {
  },

  created: function () {
    this.messagingSequenceType = this.$route.params.messagingSequenceType || 'RoomReservation';
    this.loadData();
    this.updateTemplates();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.messagingSequence.list(this, this.messagingSequenceType))
        .then((response) => {
          const tmp = response.data.data;
          tmp.forEach((it) => {
            if (isEmpty(it.offsetPeriod)) it.offsetPeriod = 1;
            else it.offsetPeriod = parseInt(it.offsetPeriod.replace(/^P(.*[A-Z])?([0-9]+)D.*$/gm, '$2'), 10);
            if (isEmpty(it.timeOfDay)) it.timeOfDay = '16:00';
            it.timeOfDay = it.timeOfDay.replace(/^([0-9]+:[0-9]+).*$/gm, '$1');
          });
          this.messagingSequences = tmp;
          this.savedMessagingSequences = cloneDeep(this.messagingSequences);
          this.$serviceBus.$emit('messagingSequence.list', this.messagingSequences);
        });
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Email'))
        .then((response) => {
          let filteredTemplates = response.data.data;

          filteredTemplates = filteredTemplates.filter(it => it.type === 'GenericEmail');
          filteredTemplates = sortBy(filteredTemplates, ['name']);

          this.templates = filteredTemplates;
        });
    },
    addPreArrivalEmail() {
      this.axios.post(this.serviceUrls.messagingSequence.list(this, this.messagingSequenceType), {
        templateId: null,
        offsetType: 'Before',
        offsetPeriod: 'P3D',
        field: 'ArrivalDate',
        timeOfDay: '16:00',
        onlySendOncePerReservationGroup: true,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    addPostDepartureEmail() {
      this.axios.post(this.serviceUrls.messagingSequence.list(this, this.messagingSequenceType), {
        templateId: null,
        offsetType: 'After',
        offsetPeriod: 'P3D',
        field: 'DepartureDate',
        timeOfDay: '16:00',
        onlySendOncePerReservationGroup: true,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    getName(idx, messagingSequence) {
      if (isNil(messagingSequence)) return '-';

      let ret = null;

      ret = `Automatische E-Mail #${idx + 1}`;

      if (this.hasRole('ROLE_SYSADMIN')) {
        ret = `${ret} <span class="color--gray">(${messagingSequence.id})</span>`;
      }

      return ret;
    },
    deleteMessagingSequence(messagingSequence, confirmed = false) {
      const messagingSequenceId = messagingSequence.id;
      if (!confirmed) {
        this.showConfirmModal(
          this.$t('admin.pageMessagingSequences.confirmDeleteMessagingSequence', [this.getName(this.messagingSequences.indexOf(messagingSequence), messagingSequence)]),
          (doNotShowAgain) => {
            this.deleteMessagingSequence(messagingSequence, true);
          },
        );
      } else {
        this.axios.delete(this.serviceUrls.messagingSequence.get(this, this.messagingSequenceType, messagingSequenceId))
          .then((response) => {
            this.loadData();
            this.$serviceBus.$emit('hotelSettings.update');
            this.showSuccessMessage();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
          });
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageMessagingSequences.vue